import { InvoicePlan } from '@wonderschool/common-base-types';
import { Button, Dialog, Input, RadioButtonGroup, TextArea } from '@wonderschool/common-base-ui';
import moment from 'moment-timezone';
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getDateEnd } from '../../../../helpers/invoicePlans';
import { currencyFormatter } from '../../../../helpers/utils';
import { useLoadedStudents } from '../../../../students/studentsHooks';
import { getBillingContactWithEmail } from '../../../../students/studentsUtils';
import { getStudentNames } from './InvoicePlanDetailSlideover';
import {
  EndPlanNotificationRequest,
  EndPlanTiming,
  EndPlanUpdate,
  ExistingPlanInvoice,
  getEndPlanChargePreview,
  getEndPlanEmailCopy,
  getEndPlanUpdate,
  hasScheduledPlanEnd,
} from './endInvoicePlan';

type EndInvoicePlanDialogProps = {
  invoicePlan: InvoicePlan;
  isOpen: boolean;
  isSaving: boolean;
  providerTimezone: string;
  schoolName: string;
  familyPaysProcessingFees?: boolean;
  existingInvoices?: ExistingPlanInvoice[];
  onClose: () => void;
  onEndPlan: (update: EndPlanUpdate) => Promise<void>;
};

export function EndInvoicePlanDialog({
  invoicePlan,
  isOpen,
  isSaving,
  providerTimezone,
  schoolName,
  familyPaysProcessingFees = false,
  existingInvoices = [],
  onClose,
  onEndPlan,
}: EndInvoicePlanDialogProps) {
  const { t } = useTranslation();
  const hasScheduledEnd = hasScheduledPlanEnd(invoicePlan);
  const existingNotification = (invoicePlan as InvoicePlan & { endPlanNotification?: EndPlanNotificationRequest })
    .endPlanNotification;
  const [timing, setTiming] = useState<EndPlanTiming>(hasScheduledEnd ? 'specificDate' : 'immediately');
  const originalEndDate = hasScheduledEnd ? moment(invoicePlan.dateEnd).tz(providerTimezone).format('YYYY-MM-DD') : '';
  const [endDate, setEndDate] = useState(originalEndDate);
  const [error, setError] = useState('');
  const [emailSubject, setEmailSubject] = useState(existingNotification?.subject ?? '');
  const [emailMessage, setEmailMessage] = useState(existingNotification?.message ?? '');
  const [sendEmail, setSendEmail] = useState(true);

  const loadedStudents = useLoadedStudents(invoicePlan.students.map((student) => student.id));

  const timingOptions = useMemo(
    () => [
      {
        name: 'end-plan-immediately',
        value: 'immediately',
        label: (
          <span className="flex flex-col">
            <span className="font-semibold text-gray-900">{t('recurringPlanList.endPlan.immediately')}</span>
            <span className="text-sm text-gray-600">{t('recurringPlanList.endPlan.immediatelyDescription')}</span>
          </span>
        ),
      },
      {
        name: 'end-plan-specific-date',
        value: 'specificDate',
        label: <span className="font-semibold text-gray-900">{t('recurringPlanList.endPlan.specificDate')}</span>,
      },
    ],
    [t]
  );

  const chargePreview = getEndPlanChargePreview({
    invoicePlan,
    timing,
    endDate,
    providerTimezone,
    existingInvoices,
  });
  const formatMoney = (amount: number) => currencyFormatter(amount, { precision: 2 });
  const studentNames = getStudentNames(invoicePlan.students);
  const firstPlanStudent = invoicePlan.students[0];
  const primaryStudent = loadedStudents.find((student) => student.id === firstPlanStudent?.id) ?? firstPlanStudent;
  const billingContact = getBillingContactWithEmail(primaryStudent);
  const minimumEndDate = moment().tz(providerTimezone).format('YYYY-MM-DD');
  const selectedEndDateEpoch = getDateEnd(endDate, providerTimezone);
  const needsEndDate = timing === 'specificDate';
  const isPastEndDate = needsEndDate && !!endDate && endDate < minimumEndDate;
  const isSubjectTooLong = emailSubject.length > 200;
  const isMessageTooLong = emailMessage.length > 5_000;
  const canSave =
    !isSaving &&
    chargePreview !== null &&
    (!needsEndDate || (!!selectedEndDateEpoch && !isPastEndDate)) &&
    (!sendEmail ||
      !billingContact?.email ||
      (!!emailSubject.trim() && !!emailMessage.trim() && !isSubjectTooLong && !isMessageTooLong));
  const handleEndDateChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEndDate(event.target.value);
    setError('');
  };

  const endDateChanged = hasScheduledEnd && endDate !== originalEndDate;

  const defaultEmailCopy = getEndPlanEmailCopy({
    t,
    parentName: billingContact?.displayName || t('recurringPlanList.endPlan.parentFallback'),
    studentName: studentNames,
    schoolName,
    endDate:
      timing === 'immediately'
        ? moment().tz(providerTimezone).format('MMMM D, YYYY')
        : moment.tz(endDate, 'YYYY-MM-DD', providerTimezone).format('MMMM D, YYYY'),
    chargeAmount: formatMoney(chargePreview?.chargeAmount ?? 0),
    invoiceCount: chargePreview?.invoiceCount ?? 0,
    familyPaysProcessingFees,
    isChange: endDateChanged,
    isImmediate: timing === 'immediately',
  });

  const emailTemplateKey = `${timing}:${endDate}:${chargePreview?.chargeAmount}:${chargePreview?.invoiceCount}`;
  const previousEmailTemplateKey = useRef(existingNotification ? emailTemplateKey : '');
  const emailMessageRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    // common-base-ui's TextArea type accepts maxLength but its current implementation
    // does not forward that attribute to the native textarea.
    if (emailMessageRef.current) emailMessageRef.current.maxLength = 5_000;
  }, []);

  useEffect(() => {
    if ((timing === 'specificDate' && !endDate) || previousEmailTemplateKey.current === emailTemplateKey) return;
    previousEmailTemplateKey.current = emailTemplateKey;
    setEmailSubject(defaultEmailCopy.subject);
    setEmailMessage(defaultEmailCopy.message);
  }, [defaultEmailCopy.message, defaultEmailCopy.subject, emailTemplateKey, endDate, timing]);

  const handleClose = () => {
    if (isSaving) return;
    setTiming('immediately');
    setEndDate('');
    setError('');
    setEmailSubject('');
    setEmailMessage('');
    setSendEmail(true);
    onClose();
  };

  const handleEndPlan = async () => {
    if (!canSave) return;

    const update = getEndPlanUpdate({ invoicePlan, timing, endDate, providerTimezone });
    if (!update) return;

    setError('');
    try {
      await onEndPlan({
        ...update,
        ...(billingContact?.email && sendEmail
          ? {
              endPlanNotification: {
                subject: emailSubject || defaultEmailCopy.subject,
                message: emailMessage || defaultEmailCopy.message,
                chargeAmount: chargePreview?.chargeAmount ?? 0,
                invoiceCount: chargePreview?.invoiceCount ?? 0,
                requestedAt: Date.now(),
              },
            }
          : { endPlanNotification: null }),
      });
      handleClose();
    } catch {
      setError(t('recurringPlanList.endPlan.error'));
    }
  };

  const primaryActionLabel = chargePreview
    ? timing === 'immediately'
      ? t('recurringPlanList.endPlan.confirm', { amount: formatMoney(chargePreview.chargeAmount) })
      : t('recurringPlanList.endPlan.schedule', { amount: formatMoney(chargePreview.chargeAmount) })
    : timing === 'immediately'
      ? t('recurringPlanList.endPlan.confirmWithoutAmount')
      : t('recurringPlanList.endPlan.scheduleWithoutAmount');

  return (
    <Dialog
      isOpen={isOpen}
      hasCloseButton
      onClose={handleClose}
      data-testid="end-invoice-plan-dialog"
      extraClasses="md:w-[36rem] md:max-w-[36rem]"
      fullScreenOnMobile
    >
      <Dialog.Panel extraClasses="space-y-5">
        <div>
          <Dialog.Title>{t('recurringPlanList.endPlan.title')}</Dialog.Title>
          <p className="mt-1 text-base font-semibold text-gray-900" data-testid="end-plan-student-names">
            {studentNames}
          </p>
          <p className="text-sm text-gray-600">
            {t('recurringPlanList.endPlan.planSummary', {
              interval: t(invoicePlan.billingInterval),
              amount: formatMoney(invoicePlan.total),
            })}
          </p>
        </div>

        <div>
          <p className="mb-3 font-semibold text-gray-900">{t('recurringPlanList.endPlan.when')}</p>
          <RadioButtonGroup
            value={timingOptions.find((option) => option.value === timing)}
            items={timingOptions}
            onChange={(option) => {
              setTiming(option.value as EndPlanTiming);
              setEmailSubject('');
              setEmailMessage('');
              setSendEmail(true);
              setError('');
            }}
            className="flex flex-col gap-4"
            data-testid="end-plan-timing"
          />
          {needsEndDate && (
            <div className="mt-3 max-w-64 pl-7">
              <Input
                name="endDate"
                label={t('recurringPlanList.endPlan.endDate')}
                inputType="date"
                min={minimumEndDate}
                value={endDate}
                onInput={handleEndDateChange}
                required
                data-testid="end-plan-date"
              />
            </div>
          )}
        </div>

        <section className="rounded-lg border border-blue-200 bg-blue-50 p-4" data-testid="end-plan-billing-impact">
          <h3 className="text-sm font-bold uppercase tracking-wide text-blue-900">
            {t('recurringPlanList.endPlan.familyCharge')}
          </h3>
          {chargePreview ? (
            <p className="mt-2 text-3xl font-bold text-gray-950" data-testid="end-plan-charge-amount">
              {formatMoney(chargePreview.chargeAmount)}
            </p>
          ) : !needsEndDate || endDate ? (
            <p className="mt-2 text-sm font-semibold text-yellow-900" data-testid="end-plan-charge-unavailable">
              {t('recurringPlanList.endPlan.chargeUnavailable')}
            </p>
          ) : null}
          <p className="mt-3 text-sm text-gray-700">
            {timing === 'immediately'
              ? t('recurringPlanList.endPlan.noAdditionalInvoices')
              : endDate
                ? t('recurringPlanList.endPlan.noInvoicesAfter', {
                    date: moment.tz(endDate, 'YYYY-MM-DD', providerTimezone).format('MM/DD/YYYY'),
                  })
                : t('recurringPlanList.endPlan.selectDatePrompt')}
          </p>
          {(chargePreview?.invoiceCount ?? 0) > 0 && (
            <>
              <p className="mt-1 text-sm text-gray-700">
                {t('recurringPlanList.endPlan.invoiceCount', { count: chargePreview?.invoiceCount })}{' '}
                {t('recurringPlanList.endPlan.notProrated')}
              </p>
              {familyPaysProcessingFees && (
                <p className="mt-1 text-sm font-medium text-gray-700">
                  {t('recurringPlanList.endPlan.processingFeeNotice')}
                </p>
              )}
            </>
          )}
        </section>

        {(timing === 'immediately' || endDate) && (
          <section className="space-y-3 border-t border-gray-200 pt-5" data-testid="end-plan-parent-email">
            {endDateChanged && (
              <p className="rounded-md bg-blue-50 p-3 text-sm font-medium text-blue-900">
                {t('recurringPlanList.endPlan.changeEmailNotice')}
              </p>
            )}
            <div>
              <h3 className="font-semibold text-gray-900">{t('recurringPlanList.endPlan.parentEmail')}</h3>
              {billingContact?.email ? (
                <div>
                  <p className="break-words text-sm text-gray-600">
                    {t('recurringPlanList.endPlan.emailTo', {
                      name: billingContact.displayName,
                      email: billingContact.email,
                    })}
                  </p>
                  {sendEmail && (
                    <div className="mt-3 flex justify-end">
                      <Button
                        label={t('recurringPlanList.endPlan.doNotSendEmail')}
                        onClick={() => setSendEmail(false)}
                        extraClasses="w-full text-red-700 sm:w-auto"
                      />
                    </div>
                  )}
                </div>
              ) : (
                <p className="mt-1 rounded-md bg-yellow-50 p-2 text-sm text-yellow-900">
                  {t('recurringPlanList.endPlan.noBillingEmail')}
                </p>
              )}
            </div>
            {billingContact?.email && sendEmail && (
              <>
                <Input
                  name="endPlanEmailSubject"
                  label={t('recurringPlanList.endPlan.emailSubject')}
                  value={emailSubject}
                  onChange={(event) => setEmailSubject(event.target.value)}
                  maxLength={200}
                  required
                />
                {isSubjectTooLong && (
                  <p className="text-sm font-medium text-red-700" role="alert">
                    {t('recurringPlanList.endPlan.emailSubjectTooLong')}
                  </p>
                )}
                <TextArea
                  ref={emailMessageRef}
                  name="endPlanEmailMessage"
                  label={t('recurringPlanList.endPlan.emailMessage')}
                  value={emailMessage}
                  rows={9}
                  resize="vertical"
                  onChange={(event) => setEmailMessage(event.target.value)}
                  maxLength={5000}
                  required
                  data-testid="end-plan-email-message"
                />
                {isMessageTooLong && (
                  <p className="text-sm font-medium text-red-700" role="alert">
                    {t('recurringPlanList.endPlan.emailMessageTooLong')}
                  </p>
                )}
                <p className="text-xs text-gray-500">{t('recurringPlanList.endPlan.sentImmediately')}</p>
              </>
            )}
            {billingContact?.email && !sendEmail && (
              <div className="flex items-center justify-between gap-4 rounded-md bg-gray-100 p-3">
                <p className="text-sm font-medium text-gray-800">{t('recurringPlanList.endPlan.emailWillNotSend')}</p>
                <Button
                  label={t('recurringPlanList.endPlan.sendEmail')}
                  onClick={() => {
                    setEmailSubject(defaultEmailCopy.subject);
                    setEmailMessage(defaultEmailCopy.message);
                    setSendEmail(true);
                  }}
                />
              </div>
            )}
          </section>
        )}

        <div className="space-y-1 text-sm text-gray-600">
          <p>{t('recurringPlanList.endPlan.enrollmentDisclaimer')}</p>
        </div>

        {error && (
          <p className="rounded-md bg-red-50 p-3 text-sm font-medium text-red-800" role="alert">
            {error}
          </p>
        )}
      </Dialog.Panel>
      <Dialog.Actions>
        <Button label={t('recurringPlanList.endPlan.keepPlan')} disabled={isSaving} onClick={handleClose} />
        <Button
          label={primaryActionLabel}
          primary
          loading={isSaving}
          disabled={!canSave}
          onClick={handleEndPlan}
          extraClasses="bg-red-700 text-white hover:bg-red-800"
          data-testid="confirm-end-plan-button"
        />
      </Dialog.Actions>
    </Dialog>
  );
}
